import React, { useEffect, useRef, useState } from "react";
import { stripFileBlocks, parseFiles } from "../../lib/parseFiles";
import GalaxyBackground from "../GalaxyBackground";
import { PaperPlaneRight, User, Lightning, CircleNotch, FileCode } from "@phosphor-icons/react";

function FileChips({ names }) {
  if (!names.length) return null;
  return (
    <div className="flex flex-wrap gap-1.5 mt-2">
      {names.map((n) => (
        <span key={n} className="inline-flex items-center gap-1 text-[11px] font-mono text-forge-primary border border-forge-primary/30 bg-forge-primary/10 px-2 py-0.5 rounded-sm">
          <FileCode size={11} /> {n}
        </span>
      ))}
    </div>
  );
}

export default function ChatPanel({ messages, streaming, streamingText, onSend }) {
  const [input, setInput] = useState("");
  const scrollRef = useRef(null);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, streamingText, streaming]);

  const submit = () => {
    const text = input.trim();
    if (!text || streaming) return;
    onSend(text);
    setInput("");
  };

  return (
    <div className="relative h-full flex flex-col bg-forge-bg overflow-hidden">
      <div className="absolute inset-0 z-0 opacity-30 pointer-events-none">
        <GalaxyBackground density={0.55} />
      </div>
      <div className="absolute inset-0 z-0 pointer-events-none bg-gradient-to-b from-forge-bg/70 via-forge-bg/55 to-forge-bg/85" />
      <div className="relative z-10 h-9 flex items-center px-3 border-b border-forge-border shrink-0 gap-2 bg-forge-bg/60 backdrop-blur-sm">
        <Lightning size={15} weight="fill" className="text-forge-primary" />
        <span className="text-xs uppercase tracking-[0.2em] text-neutral-400">AI Builder</span>
      </div>

      <div ref={scrollRef} className="relative z-10 flex-1 overflow-y-auto p-4 space-y-5">
        {messages.length === 0 && !streaming && (
          <div className="relative h-full flex flex-col items-center justify-center text-center px-4">
            <div className="w-16 h-16 rounded-full flex items-center justify-center mb-5 border border-forge-primary/40 bg-forge-primary/10 shadow-[0_0_40px_rgba(234,179,8,0.35)]">
              <Lightning size={26} weight="fill" className="text-forge-primary" />
            </div>
            <p className="text-sm text-neutral-300 max-w-[240px]">
              Describe a feature or an app and watch galaxies of code emerge in real time.
            </p>
          </div>
        )}

        {messages.map((m, i) => {
          const isUser = m.role === "user";
          const cleaned = isUser ? m.content : stripFileBlocks(m.content);
          const fileNames = isUser ? [] : parseFiles(m.content).map((f) => f.name);
          return (
            <div key={i} className="animate-fade-up" data-testid={`chat-message-${m.role}`}>
              <div className="flex items-center gap-2 mb-1.5">
                <div className={`w-5 h-5 flex items-center justify-center rounded-sm ${isUser ? "bg-forge-hover" : "bg-forge-primary"}`}>
                  {isUser ? <User size={12} className="text-neutral-300" /> : <Lightning size={12} weight="fill" className="text-black" />}
                </div>
                <span className="text-[11px] uppercase tracking-[0.15em] text-neutral-500">
                  {isUser ? "You" : "Matrix"}
                </span>
              </div>
              <div className={`text-sm leading-relaxed whitespace-pre-wrap pl-7 ${isUser ? "text-neutral-200" : "text-neutral-300"}`}>
                {cleaned || (isUser ? m.content : "")}
                {!isUser && <FileChips names={fileNames} />}
              </div>
            </div>
          );
        })}

        {streaming && (
          <div className="animate-fade-up" data-testid="chat-streaming">
            <div className="flex items-center gap-2 mb-1.5">
              <div className="w-5 h-5 flex items-center justify-center rounded-sm bg-forge-primary animate-pulse-glow">
                <Lightning size={12} weight="fill" className="text-black" />
              </div>
              <span className="text-[11px] uppercase tracking-[0.15em] text-forge-primary flex items-center gap-1">
                <CircleNotch size={11} className="animate-spin" /> Building
              </span>
            </div>
            <div className="text-sm leading-relaxed whitespace-pre-wrap pl-7 text-neutral-300">
              {streamingText || <span className="text-neutral-500 font-mono text-xs">generating code…</span>}
            </div>
          </div>
        )}
      </div>

      <div className="relative z-10 p-3 border-t border-forge-border shrink-0 bg-forge-bg/60 backdrop-blur-sm">
        <div className="border border-forge-border bg-forge-surface rounded-sm p-2 focus-within:ring-1 focus-within:ring-forge-primary focus-within:border-forge-primary transition-colors">
          <textarea
            data-testid="chat-input"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                submit();
              }
            }}
            rows={2}
            disabled={streaming}
            placeholder={streaming ? "Building..." : "Ask for changes or a new feature..."}
            className="w-full bg-transparent resize-none text-sm text-white placeholder:text-neutral-500 focus:outline-none px-1 disabled:opacity-60"
          />
          <div className="flex justify-end pt-1">
            <button
              data-testid="chat-submit-button"
              onClick={submit}
              disabled={streaming || !input.trim()}
              className="flex items-center gap-1.5 bg-forge-primary text-black text-sm font-medium px-3 py-1.5 rounded-sm hover:bg-yellow-400 transition-colors disabled:opacity-50"
            >
              {streaming ? <CircleNotch size={14} className="animate-spin" /> : <PaperPlaneRight size={14} weight="fill" />}
              Send
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
